import React, { FocusEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FieldInputProps, FormikErrors } from 'formik';
import isEmpty from 'lodash/isEmpty';
import styled, { css } from 'styled-components';
import * as API from 'api';
import { Alert, Banner, Button, ButtonText, FieldWrapper, FormError, H4, Input, Modal, ModalAvatar, ModalFooter, ModalForm, ModalFormLayout, ModalHeader, ModalText, ModalTitle, Select, Text } from 'components';
import { ICreatableSelectOption } from 'components/CreatableSelect/CreatableSelect';
import { JobTitleCreatableSelect } from 'components/JobTitleCreatableSelect/JobTitleCreatableSelect';
import { DDOption, DisbursementOptions, DismissReasons, SiteRoleOptions, UserRoleOptions } from 'config/const';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { selectAPIRequestErrors } from 'store/ApiClient';
import { Employee } from 'store/Employee';
import { selectCurrentEmployeeType } from 'store/Employers';
import { IJobTitleState, IStore, IUserState } from 'store/types';
import { ApiQueryOperator, EmployeeActionRequiredType, EmployeeStatus, EmployeeType, IApiErrorPayloadWithCode, IEmployee, IEmployeeIntegration, IEmployeeSite, ISite, PayoutStatus } from 'types';
import { isEmployeeIdRequired } from 'utils';
import { capitalize } from 'utils/strings';
import AlertHeader from '../components/Alert/AlertHeader';
import { HeaderContent, InvitationNotice, RemoveButton, ReviewNowButton, UnlinkButton } from './styles';

const StyledModalHeader = styled(ModalHeader)`
  padding-bottom: ${toRem(33)};
`;
const CustomBanner = styled(Banner)`
  border-radius: 11px;
  margin-bottom: 33px;

  a {
    color: #252727 !important;
  }
`;

const StyledAlertModalHeader = styled(ModalHeader)`
  padding-bottom: ${toRem(26)};
`;

const IntegrationItem = styled.div`
  width: 100%;
  display: flex;
  padding: ${toRem(12)};
  background-color: #fff;
  border-radius: ${toRem(theme.borderRadius.xsmall)};
  border: ${toRem(1)} solid ${theme.color.neutral[300]};
  flex-flow: row;
  flex-wrap: nowrap;
  justify-content: space-between;
`;

const JobTitleField = styled(JobTitleCreatableSelect)`
  ${({ disabled }) =>
    disabled &&
    css`
      div,
      input {
        background-color: ${theme.color.neutral[100]} !important;
      }
    `}
`;

interface IEditSiteProps {
  currentOrganizationName: string;
  currentUser: IUserState;
  dirty: boolean;
  employeeDetail?: IEmployee;
  employeeSiteInfo: EmployeeInfo;
  errors: FormikErrors<{
    role?: {
      label: string;
      value: string;
    };
    jobTitle?: ICreatableSelectOption<IJobTitleState>;
    employeeId?: string;
  }>;
  getFieldProps: (field: string) => FieldInputProps<any>;
  handleBlur?: {
    (e: FocusEvent<any>): void;
    <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void;
  };
  isActive: boolean;
  isLoading: boolean;
  isRemoveLoading: boolean;
  isUnlinkIntegrationLoading: boolean;
  isSiteRole: boolean;
  isValid: boolean;
  onActivateSite: () => void;
  onDismissSite: (dismissReason: string) => void;
  onClose: () => void;
  onRemoveSite: () => void;
  onUnlinkIntegration: (integration: IEmployeeIntegration) => void;
  onSubmit: () => void;
  setFieldValue: (fieldName: string, value: any) => void;
  showReviewModal: () => void;
  site: IEmployeeSite;
  sitesWithSettings?: ISite[];
  validateField: (field: 'role' | 'jobTitle' | 'employeeId' | 'disbursementMethod') => boolean;
}

interface EmployeeInfo {
  role?: DDOption;
  job?: IJobTitleState;
  disbursementMethod?: DDOption;
  employeeId?: string;
  siteName: string;
  status: string;
}

const EditSite: React.FC<IEditSiteProps> = ({
  currentOrganizationName,
  currentUser,
  dirty,
  employeeDetail,
  employeeSiteInfo,
  errors,
  getFieldProps,
  handleBlur,
  isActive,
  isLoading,
  isRemoveLoading,
  isSiteRole,
  isValid,
  onActivateSite,
  onDismissSite,
  onClose,
  onRemoveSite,
  onUnlinkIntegration,
  isUnlinkIntegrationLoading,
  onSubmit,
  setFieldValue,
  showReviewModal,
  site,
  sitesWithSettings,
  validateField,
}) => {
  const [editStep, setEditStep] = useState<'initial' | 'activate' | 'paymentsAlert' | 'dismiss' | 'remove' | 'hasOutstandingPayments' | 'unlink'>('initial');

  const [selectedIntegration, setSelectedIntegration] = useState<IEmployeeIntegration>(undefined);
  const [outstandingPaymentsCount, setOutstandingPaymentsCount] = useState<number>();
  const [dismissReason, setDismissReason] = useState<DDOption>();

  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Employee.UPDATE_EMPLOYEE_SITE_ROLE_REQUEST)) as IApiErrorPayloadWithCode;

  const cancelDismissal = () => {
    setDismissReason(undefined);
    setEditStep('initial');
  };

  // User/Employee Meta
  const employeeName = employeeDetail?.name || '';
  const employeeAvatar = employeeDetail?.user?.avatarUrl;
  const employeeHasPaymentMethod = !!employeeDetail?.user?.card;

  // UI Vars
  const removeUser = () => {
    onRemoveSite();
  };

  const activateSite = () => {
    onActivateSite();
  };

  const fullName = `${employeeDetail?.firstName} ${employeeDetail?.lastName}`;
  const actionRequired = employeeDetail?.actionRequired;
  const invitationStatus = employeeDetail?.status;
  const isSameLoggedUser = employeeDetail?.user?.id === currentUser?.id;
  const isPending = invitationStatus === EmployeeStatus.INVITED;
  const isExpired = invitationStatus === EmployeeStatus.ACTION_REQUIRED && actionRequired === EmployeeActionRequiredType.INVITE_EXPIRED;
  const shouldDisplayInviteMethod = isPending || isExpired;
  const isPendingReview = actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL && invitationStatus === EmployeeStatus.ACTION_REQUIRED;
  const isDismissed = invitationStatus === EmployeeStatus.DISMISSED;
  const siteApproved = employeeSiteInfo.status === EmployeeStatus.APPROVED;
  const siteDismissed = employeeSiteInfo.status === EmployeeStatus.DISMISSED;
  const shouldDisplayRoleSelector = employeeDetail?.user?.id !== currentUser?.id;

  const currentEmployeeType = useSelector(selectCurrentEmployeeType);

  const getOutstandingPayments = useCallback(async (siteId: string, empId: string) => {
    try {
      const response = await API.Sites.getEmployeePayouts(siteId, empId, {
        limit: 1,
        q: [{ key: 'status', operator: ApiQueryOperator.in, value: [PayoutStatus.PENDING, PayoutStatus.EXPIRED] }],
      });
      setOutstandingPaymentsCount(response.body.total);
    } catch {
      setOutstandingPaymentsCount(0);
    }
  }, []);

  useEffect(() => {
    if (employeeDetail && site) {
      getOutstandingPayments(site.id, employeeDetail.id);
    }
  }, [employeeDetail?.id, site]);

  // Get employee integrations
  const employeeIntegrations = employeeDetail.integrations?.filter((integration) => integration.siteId === site.id);

  // TODO: After the modal factory is open for business,
  // refactor this so there's no flicker between the modal and alert
  return (
    <>
      {editStep === 'activate' && (
        <Modal isActive={isActive} onClose={onClose}>
          <ModalHeader style={{ paddingBottom: '0' }}>
            <HeaderContent>
              <AlertHeader icon="CheckmarkThick" iconFill={theme.color.success.medium} title={`Activate user on ${employeeSiteInfo.siteName}?`} />
              <ModalText>Activating this user will enable them to receive payments at this site.</ModalText>
            </HeaderContent>
          </ModalHeader>
          <ModalForm>
            <ModalFooter columns={2} centerButtons>
              <Button label="Cancel" appearance="white" size="large" onClick={onClose} />
              <Button label="Activate" disabled={false} isProcessing={isLoading} type="button" appearance="primary" size="large" onClick={activateSite} />
            </ModalFooter>
          </ModalForm>
        </Modal>
      )}
      {editStep === 'remove' && (
        <Alert
          isActive={isActive}
          title={`Remove user from ${employeeSiteInfo.siteName}?`}
          message="You will not be able to send payments from this site."
          leftActionLabel="Cancel"
          onLeftAction={() => {
            setEditStep('initial');
          }}
          rightActionLabel="Remove"
          rightActionAppearance="destructive"
          onRightAction={removeUser}
          isLoading={isRemoveLoading}
        />
      )}
      {editStep === 'unlink' && (
        <Alert
          isActive={isActive}
          title={`You are about to disconnect ${employeeDetail.name} from ${capitalize(selectedIntegration.type)}`}
          message="You must issue a payment to this user in order to connect them to the integration again."
          leftActionLabel="Cancel"
          onLeftAction={() => {
            setEditStep('initial');
          }}
          rightActionLabel="Disconnect"
          rightActionAppearance="destructive"
          onRightAction={() => onUnlinkIntegration(selectedIntegration)}
          isLoading={isUnlinkIntegrationLoading}
        />
      )}
      {editStep === 'paymentsAlert' && (
        <Modal maxWidth={420} isActive={isActive} onClose={onClose}>
          <ModalHeader>
            <ModalTitle>
              <AlertHeader title="User Has Outstanding Payments" />
            </ModalTitle>
            <HeaderContent>
              <ModalText>
                Users cannot be {siteApproved ? 'dismissed' : 'removed'} while payments are pending or expired. Would you like to cancel all outstanding payments and {siteApproved ? 'dismiss' : 'remove'} this user now?
              </ModalText>
            </HeaderContent>
          </ModalHeader>

          <ModalFooter columns={1}>
            <Button
              label={`Cancel Payments And ${siteApproved ? 'Dismiss' : 'Remove'} User`}
              onClick={() => {
                setEditStep(siteApproved ? 'dismiss' : 'remove');
              }}
              appearance="primary"
              size="large"
              data-cy="cancel-payments-button"
            />
          </ModalFooter>
        </Modal>
      )}
      {editStep === 'dismiss' && (
        <Modal maxWidth={540} isActive={isActive} onClose={onClose}>
          <StyledAlertModalHeader>
            <ModalAvatar image={employeeAvatar} username={employeeName} hasPaymentMethod={employeeHasPaymentMethod} />
            <HeaderContent>
              <ModalTitle>{`Dismiss ${fullName} From ${employeeSiteInfo.siteName}`}</ModalTitle>
              <ModalText>Dismissed users will no longer be able to receive payments at the selected site. You can reactivate or delete this employee later.</ModalText>
            </HeaderContent>
          </StyledAlertModalHeader>

          <ModalForm>
            <ModalFormLayout>
              <FieldWrapper label="Reason for Dismissal">
                <Select placeholder="Choose a reason" options={DismissReasons} values={dismissReason ? [dismissReason] : []} onChange={(selected) => setDismissReason(selected?.[0])} data-cy="dismiss-reason-input" />
              </FieldWrapper>
              {apiErrors?.errorMessages && apiErrors?.errorMessages.map((error, index) => <FormError key={`error-message-${index}`}>{error}</FormError>)}
            </ModalFormLayout>
            <ModalFooter columns={2} centerButtons>
              <Button label="Cancel" appearance="white" size="large" onClick={cancelDismissal} />
              <Button label="Dismiss" onClick={() => dismissReason && onDismissSite(dismissReason.value)} type="button" disabled={!dismissReason} isProcessing={isLoading} appearance="destructive" data-cy="dismiss-user-button" />
            </ModalFooter>
          </ModalForm>
        </Modal>
      )}
      {editStep === 'initial' && (
        <Modal maxWidth={540} isActive={isActive} onClose={onClose}>
          <StyledModalHeader>
            <ModalAvatar image={employeeAvatar || ''} username={fullName} hasPaymentMethod={employeeHasPaymentMethod} />
            <HeaderContent>
              <ModalTitle>{fullName}</ModalTitle>
              <H4>{employeeSiteInfo.siteName}</H4>
            </HeaderContent>
          </StyledModalHeader>

          {invitationStatus === EmployeeStatus.DISMISSED ||
            (siteDismissed && (
              <CustomBanner data-cy="alert-banner">
                <H4>
                  User is dismissed at this site.&nbsp;
                  <ButtonText
                    label="Reactivate user"
                    onClick={() => {
                      setEditStep('activate');
                    }}
                  />
                </H4>
              </CustomBanner>
            ))}
          {shouldDisplayInviteMethod && <InvitationNotice appearance={Appearance.WARNING} message={`Invitation to ${currentOrganizationName} is ${isPending ? 'pending' : 'expired'}.`} />}
          {isPendingReview ? (
            <>
              <InvitationNotice
                appearance={Appearance.WARNING}
                message={
                  <>
                    <p>User is pending review.</p>
                    <ReviewNowButton label="Review Now" onClick={showReviewModal} />.
                  </>
                }
              />
            </>
          ) : null}

          {Number.isFinite(outstandingPaymentsCount) && (
            <ModalForm onSubmit={onSubmit}>
              <ModalFormLayout disabled={siteDismissed}>
                {shouldDisplayRoleSelector && (
                  <FieldWrapper label="Role" message={validateField('role') ? errors.role?.value : ''} hasError={validateField('role')}>
                    <Select
                      dataCy="role-selector"
                      options={currentEmployeeType === EmployeeType.ADMIN ? UserRoleOptions : SiteRoleOptions}
                      values={isEmpty(employeeSiteInfo?.role) ? [] : [employeeSiteInfo.role]}
                      onChange={(selected) => setFieldValue('role', selected?.[0])}
                      disabled={isLoading || siteDismissed}
                    />
                  </FieldWrapper>
                )}
                {isSiteRole && (
                  <>
                    <FieldWrapper label="Job Title" message={validateField('jobTitle') ? (errors.jobTitle as string) : ''} hasError={validateField('jobTitle')}>
                      <JobTitleField
                        dataCy="job-title-selector"
                        createNewLabel="Add {search} as a new job title"
                        defaultValue={employeeSiteInfo.job?.title}
                        disabled={!site || isLoading || siteDismissed}
                        onInputBlur={handleBlur('jobTitle')}
                        placeholder="Select Job Title"
                        siteId={site.id}
                        onChange={(selected) => {
                          setFieldValue('jobTitle', selected);
                        }}
                      />
                    </FieldWrapper>

                    <FieldWrapper data-cy="disbursement-field" label="Disbursement Method" hasError={validateField('disbursementMethod')}>
                      <Select
                        data-cy="disbursement-input"
                        disabled={isLoading || siteDismissed}
                        placeholder="Select Disbursement Method"
                        options={DisbursementOptions}
                        onChange={(selected) => {
                          setFieldValue('disbursementMethod', selected?.[0]);
                        }}
                        values={employeeSiteInfo.disbursementMethod ? [employeeSiteInfo.disbursementMethod] : []}
                      />
                    </FieldWrapper>

                    <FieldWrapper label="Employee ID" optional={!isEmployeeIdRequired(site.id, sitesWithSettings)} message={validateField('employeeId') ? errors.employeeId : ''} hasError={validateField('employeeId')}>
                      <Input data-cy="employee-id-input" placeholder="Employee ID" maxLength={50} disabled={isLoading || siteDismissed} {...getFieldProps('employeeId')} />
                    </FieldWrapper>

                    {employeeIntegrations?.length > 0 && (
                      <FieldWrapper label="Integrations">
                        {employeeIntegrations.map((integration) => (
                          <IntegrationItem key={integration.id}>
                            <Text size="small" capitalize>
                              {' '}
                              {integration.type}
                            </Text>
                            <UnlinkButton
                              fontSize={11}
                              label="Disconnect"
                              onClick={() => {
                                setSelectedIntegration(integration);
                                setEditStep('unlink');
                              }}
                              data-cy="unlink-integration-button"
                            />
                          </IntegrationItem>
                        ))}
                      </FieldWrapper>
                    )}
                  </>
                )}

                {!isSameLoggedUser && (
                  <RemoveButton
                    fontWeight={theme.font.weight.medium}
                    fontSize={11}
                    label={`${siteApproved ? 'Dismiss' : 'Remove'} User From Site`}
                    onClick={() => {
                      if (outstandingPaymentsCount) {
                        setEditStep('paymentsAlert');
                      } else {
                        setEditStep(siteApproved ? 'dismiss' : 'remove');
                      }
                    }}
                    data-cy={`${siteApproved ? 'dismiss' : 'remove'}-user-button`}
                  />
                )}
              </ModalFormLayout>

              <ModalFooter columns={isDismissed ? 1 : 2}>
                <Button label="Close" appearance="white" size="large" onClick={onClose} />
                {!isDismissed && <Button label="Update" disabled={isLoading || !(isValid && dirty)} isProcessing={isLoading} type="submit" appearance="primary" size="large" />}
              </ModalFooter>
            </ModalForm>
          )}
        </Modal>
      )}
    </>
  );
};

export default EditSite;
